import React, { useState, useRef } from 'react';
import { FiMic, FiType, FiTrash2 } from 'react-icons/fi';
import { AudioRecorder } from './AudioRecorder';
import { AudioMessage } from './AudioMessage';
import { LoadingSpinner } from './LoadingSpinner';
import {
  BroadcastMessageVariantUi,
  createEmptyBroadcastVariant,
  isBroadcastVariantValid,
  revokeVariantPreviewUrl,
} from '../utils/broadcastMessageVariantUtils';
import './BroadcastMessageVariantEditor.css';

export interface BroadcastMessageVariantEditorProps {
  variants: BroadcastMessageVariantUi[];
  onChange: (variants: BroadcastMessageVariantUi[]) => void;
  onOptimizeFirst?: () => void;
  aiOptimizing?: boolean;
  onInsertVariable?: (variable: string, index: number) => void;
  previewExampleName?: string;
}

export const BroadcastMessageVariantEditor: React.FC<BroadcastMessageVariantEditorProps> = ({
  variants,
  onChange,
  onOptimizeFirst,
  aiOptimizing = false,
  onInsertVariable,
  previewExampleName = 'John',
}) => {
  const [recordingIndex, setRecordingIndex] = useState<number | null>(null);
  const [focusedIndex, setFocusedIndex] = useState(0);
  const textareaRefs = useRef<(HTMLTextAreaElement | null)[]>([]);

  const updateVariant = (index: number, patch: Partial<BroadcastMessageVariantUi>) => {
    onChange(variants.map((v, i) => (i === index ? { ...v, ...patch } : v)));
  };

  const setMediaType = (index: number, mediaType: 'text' | 'audio') => {
    const v = variants[index];
    if (v.media_type === mediaType) return;
    if (mediaType === 'text') {
      revokeVariantPreviewUrl(v);
      updateVariant(index, {
        media_type: 'text',
        audioBlob: undefined,
        audioPreviewUrl: undefined,
        media_url: undefined,
        media_mime_type: undefined,
      });
    } else {
      updateVariant(index, { media_type: 'audio', content: '' });
    }
  };

  const handleAddVariant = () => {
    onChange([...variants, createEmptyBroadcastVariant()]);
  };

  const handleRemoveVariant = (index: number) => {
    if (variants.length <= 1) return;
    revokeVariantPreviewUrl(variants[index]);
    onChange(variants.filter((_, i) => i !== index));
  };

  const handleRecordingComplete = (index: number, blob: Blob) => {
    const prev = variants[index];
    revokeVariantPreviewUrl(prev);
    const previewUrl = URL.createObjectURL(blob);
    updateVariant(index, {
      audioBlob: blob,
      audioPreviewUrl: previewUrl,
      media_url: undefined,
      media_mime_type: 'audio/webm',
    });
    setRecordingIndex(null);
  };

  const clearAudio = (index: number) => {
    const prev = variants[index];
    revokeVariantPreviewUrl(prev);
    updateVariant(index, {
      audioBlob: undefined,
      audioPreviewUrl: undefined,
      media_url: undefined,
      media_mime_type: undefined,
    });
  };

  const previewText = (text: string) => text.replace(/\{name\}/g, previewExampleName);

  const textVariantsWithName = variants.filter(v => v.media_type === 'text' && v.content.includes('{name}'));

  return (
    <div className="broadcast-variant-editor">
      <div className="form-hint" style={{ marginBottom: 6 }}>
        Add multiple message variants. They will be alternated between recipients to look more natural.
      </div>
      <div className="form-hint" style={{ marginBottom: 8, fontSize: 12 }}>
        Spreadsheet variables (click to insert into text variants):
      </div>
      {onInsertVariable && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 10 }}>
          <button
            type="button"
            onClick={() => onInsertVariable('{name}', focusedIndex)}
            style={{
              padding: '6px 12px',
              borderRadius: 8,
              border: '1px solid var(--primary-color)',
              background: 'var(--bg-elevated)',
              color: 'var(--text-primary)',
              fontSize: 13,
              cursor: 'pointer',
              fontFamily: 'monospace',
            }}
          >
            {'{name}'}
          </button>
        </div>
      )}

      <div className="message-templates">
        {variants.map((variant, index) => (
          <div key={variant.id} className="message-template-item broadcast-variant-item">
            <div className="message-template-number">{index + 1}</div>

            <div className="broadcast-variant-type-toggle">
              <button
                type="button"
                className={variant.media_type === 'text' ? 'active' : ''}
                onClick={() => setMediaType(index, 'text')}
                title="Text message"
              >
                <FiType size={14} /> Text
              </button>
              <button
                type="button"
                className={variant.media_type === 'audio' ? 'active' : ''}
                onClick={() => setMediaType(index, 'audio')}
                title="Audio message"
              >
                <FiMic size={14} /> Audio
              </button>
            </div>

            {variant.media_type === 'text' ? (
              index === 0 && onOptimizeFirst ? (
                <div className="field-with-optimize broadcast-variant-body">
                  <div className="field-with-optimize__header">
                    <button
                      type="button"
                      className="field-with-optimize__btn"
                      title="Optimize with AI"
                      disabled={aiOptimizing}
                      onClick={onOptimizeFirst}
                    >
                      {aiOptimizing ? <LoadingSpinner size="sm" color="white" /> : 'Optimize'}
                    </button>
                  </div>
                  <textarea
                    ref={el => { textareaRefs.current[index] = el; }}
                    className="field-with-optimize__textarea"
                    onFocus={() => setFocusedIndex(index)}
                    placeholder={`Message variant ${index + 1}...`}
                    value={variant.content}
                    onChange={e => updateVariant(index, { content: e.target.value })}
                  />
                </div>
              ) : (
                <textarea
                  ref={el => { textareaRefs.current[index] = el; }}
                  className="broadcast-variant-body"
                  onFocus={() => setFocusedIndex(index)}
                  placeholder={`Message variant ${index + 1}...`}
                  value={variant.content}
                  onChange={e => updateVariant(index, { content: e.target.value })}
                />
              )
            ) : (
              <div className="broadcast-variant-audio">
                {recordingIndex === index ? (
                  <AudioRecorder
                    onSend={blob => handleRecordingComplete(index, blob)}
                    onCancel={() => setRecordingIndex(null)}
                  />
                ) : variant.audioPreviewUrl || variant.media_url ? (
                  <div className="broadcast-variant-audio-preview">
                    <AudioMessage
                      src={variant.audioPreviewUrl || variant.media_url || ''}
                      isSent={false}
                    />
                    <div className="broadcast-variant-audio-actions">
                      <button type="button" onClick={() => setRecordingIndex(index)}>
                        Re-record
                      </button>
                      <button type="button" onClick={() => clearAudio(index)}>
                        Remove audio
                      </button>
                    </div>
                  </div>
                ) : (
                  <button
                    type="button"
                    className="broadcast-variant-record-btn"
                    onClick={() => setRecordingIndex(index)}
                  >
                    <FiMic size={16} /> Record audio
                  </button>
                )}
              </div>
            )}

            {variants.length > 1 && (
              <button
                type="button"
                className="message-template-remove"
                onClick={() => handleRemoveVariant(index)}
                title="Remove variant"
              >
                <FiTrash2 size={16} />
              </button>
            )}
          </div>
        ))}

        <button type="button" className="add-message-btn" onClick={handleAddVariant}>
          + Add message variant
        </button>
      </div>

      {textVariantsWithName.length > 0 && (
        <div style={{ marginTop: 12, padding: '10px 12px', background: 'var(--bg-tertiary)', borderRadius: 8 }}>
          <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-secondary)', marginBottom: 6 }}>
            Preview (example with name &quot;{previewExampleName}&quot;):
          </div>
          {textVariantsWithName.map(v => (
            <div key={v.id} style={{ fontSize: 13, color: 'var(--text-primary)', marginBottom: 4 }}>
              {previewText(v.content)}
            </div>
          ))}
        </div>
      )}

      {variants.some(v => !isBroadcastVariantValid(v)) && (
        <div className="form-hint" style={{ marginTop: 8, color: 'var(--warning-color)' }}>
          Each variant needs text or a recorded audio clip.
        </div>
      )}
    </div>
  );
};

export default BroadcastMessageVariantEditor;
